import {
  AccountDocument,
  AccountMilestoneWithRequirements,
  AccountRequirementStatus,
  DomesticContentEligibilityTypes,
  MilestoneRequirement,
  QueueDocumentResponse,
  QueueStatus,
  RequirementsWithRequirements,
} from 'types';

/**
 * Client-side mirror of the server's `isDomesticContentEffectivelyConfirmed`.
 *
 * The server rejects an unacknowledged opt-out for an account holding an approved domestic content
 * queue and a completed confirmation requirement, whatever the stored status says. The UI has to
 * reach the same verdict so the reviewer sees the warning and the request carries the
 * acknowledgement — a server-only widening would hard-400 them with no way to proceed.
 */
export const isDomesticContentEffectivelyConfirmedForAccount = (
  account?: AccountDocument,
  domesticContentQueue?: QueueDocumentResponse,
): boolean => {
  if (account?.domesticContentEligibilityStatus === DomesticContentEligibilityTypes.confirmed) {
    return true;
  }
  if (account?.domesticContentEligibilityStatus === DomesticContentEligibilityTypes.epcOptOut) {
    return false;
  }

  if (domesticContentQueue?.status !== QueueStatus.approved) {
    return false;
  }

  // The confirmation requirement hangs off installPackage, but check both levels: the server
  // predicate queries accountRequirements flat, so nesting depth must not change the answer.
  return Boolean(
    account?.milestones?.some((milestone: AccountMilestoneWithRequirements) =>
      milestone.requirements?.some(
        (requirement: RequirementsWithRequirements) =>
          isCompletedDomesticContentConfirmation(requirement) ||
          requirement.requirements?.some(isCompletedDomesticContentConfirmation),
      ),
    ),
  );
};

const isCompletedDomesticContentConfirmation = (requirement: RequirementsWithRequirements): boolean =>
  requirement.type === MilestoneRequirement.domesticContentConfirmation &&
  requirement.status === AccountRequirementStatus.completed;
